import type { GoldenView } from '@tabel/shared/schemas/rules';
import { useQuery } from '@tanstack/react-query';
import { ClipboardCheck } from 'lucide-react';
import type { ReactNode } from 'react';
import { Card, EmptyState, Skeleton } from '../../components/ui';
import { api, rulesKeys } from '../../lib/api';
import { reportClientError } from '../../lib/errors';
import { ruDate } from '../../lib/format';
import { useRole } from '../../lib/role';
import { LoadError, shouldReport } from './LoadError';
import { localDate } from './dates';
import { goldenText, goldenWillChangeText } from './golden';
import { LABEL, T, say } from './texts';

/*
 * Вид «Проверочные примеры» (004, US5, T095; contracts/ui.md, «Вид «Проверочные примеры»»; FR-052): все примеры
 * объекта — из репозитория и закреплённые администратором правил. Таблица: название, дата дня, кто и когда закрепил,
 * ожидаемый результат; при черновике — «совпадает» или «изменится: было → станет» (и «Так и должно быть», если
 * отмечено). Вид — для администратора правил и утверждающего (только чтение). На телефоне строки — карточки.
 * Результаты считает сервер; интерфейс только пишет их словами (`golden.ts`).
 */

/** Ячейка: на телефоне строка таблицы — карточка, у ячейки — своя подпись. */
function Cell({ label, children, strong }: { label: string; children: ReactNode; strong?: boolean }) {
  return (
    <td
      className={`px-4 py-3 align-top max-sm:block max-sm:px-0 max-sm:py-0.5 ${strong ? 'font-medium text-ink' : 'text-ink-2'}`}
    >
      <span className="text-ink-3 sm:hidden">{`${label}: `}</span>
      {children}
    </td>
  );
}

/** По черновику: совпадает или изменится; отметка «Так и должно быть». */
function DraftResult({ g }: { g: GoldenView }) {
  const d = g.draft;
  if (!d) return null;
  if (d.passed) return <span className="text-ok">{T.goldenSame}</span>;
  return (
    <span className="text-warn">
      {goldenWillChangeText(d.before, d.after)}
      {d.confirmed && <span className="block text-ink-2">{T.goldenConfirmed}</span>}
    </span>
  );
}

export function GoldensView({ tzOffset }: { tzOffset: string }) {
  const { role } = useRole();
  const q = useQuery({
    queryKey: rulesKeys.goldens(role),
    queryFn: async () => {
      try {
        return await api.goldens();
      } catch (e) {
        if (shouldReport(e)) reportClientError('Rules', 'load goldens', e);
        throw e;
      }
    },
  });
  if (q.isLoading)
    return (
      <div aria-busy="true" aria-label={LABEL.loading} className="space-y-3">
        <Skeleton className="h-10 w-full rounded-card" />
        <Skeleton className="h-32 w-full rounded-card" />
      </div>
    );
  if (q.error)
    return <LoadError error={q.error} title={LABEL.goldensError} onRetry={() => void q.refetch()} />;
  const list = q.data ?? [];
  if (list.length === 0)
    return (
      <Card>
        <EmptyState icon={ClipboardCheck} title={T.goldensEmpty} description={T.goldensEmptyHint} />
      </Card>
    );
  const withDraft = list.some((g) => g.draft !== undefined);
  const head = [LABEL.goldenTitle, LABEL.dayDate, LABEL.pinnedBy, LABEL.expected];
  return (
    <div className="space-y-3">
      <p className="text-[13px] text-ink-2">{T.goldensIntro}</p>
      <Card className="overflow-hidden">
        <table
          aria-label={LABEL.goldens}
          className="w-full border-collapse text-left text-[13.5px] max-sm:block"
        >
          <thead className="bg-subtle text-[12px] text-ink-2 max-sm:hidden">
            <tr>
              {[...head, ...(withDraft ? [LABEL.byDraft] : [])].map((h) => (
                <th key={h} scope="col" className="px-4 py-2 font-medium">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-line max-sm:block">
            {list.map((g) => (
              <tr key={g.id} className="max-sm:block max-sm:px-4 max-sm:py-3">
                <Cell label={LABEL.goldenTitle} strong>
                  <span className="break-words">{g.title ?? g.code}</span>
                </Cell>
                <Cell label={LABEL.dayDate}>
                  <span className="whitespace-nowrap tabular-nums">{ruDate(g.date)}</span>
                </Cell>
                <Cell label={LABEL.pinnedBy}>
                  {say.pinnedBy(g.createdBy?.name ?? LABEL.team, localDate(g.createdAt, tzOffset))}
                </Cell>
                <Cell label={LABEL.expected}>
                  <span className="tabular-nums text-ink">{goldenText(g.expected)}</span>
                </Cell>
                {withDraft && (
                  <Cell label={LABEL.byDraft}>
                    <DraftResult g={g} />
                  </Cell>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
